import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { sanitiseHtml } from "../utils/sanitiseHtml";
import { rewriteToCloudFront } from "../images";
import DynamicComponent from "./DynamicComponent";
import { fetchComponentCode } from "../api";
import { postBodyStyles } from "../styles/postBody";

export default function PreviewPost({ data, isAdmin = false }) {
  const {
    text = "",
    imageUrl,
    imagePrompt,
    componentSource,
    componentId,
    profileName = "",
    profileImage = "",
  } = data || {};
  // keep a local copy that updates whenever the user edits the field
  const [source, setSource] = useState(null);

  // More sophisticated URL validation
  const isValidUrl = (string) => {
    if (!string || typeof string !== "string") return false;
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  useEffect(() => {
    setSource(componentSource || null);
  }, [componentSource]);

  const safeHtml = sanitiseHtml(text);

  /* if user pasted { componentId:"cmp-…" } fetch its source */
  useEffect(() => {
    if (source || !componentId) return;
    (async () => {
      try {
        const { source: src } = await fetchComponentCode(componentId);
        setSource(src);
      } catch (e) {
        console.error("preview comp fetch:", e);
      }
    })();
  }, [componentId, source]);

  return (
    <Box
      sx={{
        mt: 3,
        p: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        backgroundColor: "rgba(255,255,255,0.02)",
      }}
    >
      {/* header — avatar + name (always visible, with fall‑backs) */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1.5 }}>
        <img
          src={isValidUrl(profileImage) ? profileImage : "/logo.ico"}
          alt=""
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
            filter: isValidUrl(profileImage)
              ? "none"
              : "brightness(0) invert(1)",
          }}
        />
        <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
          {profileName || "Profile name"}
        </Typography>
      </Box>

      {/* post body  */}
      <Box sx={postBodyStyles} dangerouslySetInnerHTML={{ __html: safeHtml }} />

      {/* image or prompt  */}
      {imageUrl ? (
        <Box sx={{ mt: 1 }}>
          <img
            src={
              /amazonaws\.com|cloudfront\.net/.test(imageUrl)
                ? rewriteToCloudFront(imageUrl) // our own uploads
                : imageUrl // any external URL – leave untouched
            }
            alt=""
            style={{ maxWidth: "100%", borderRadius: 8 }}
          />
        </Box>
      ) : imagePrompt ? (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, fontStyle: "italic", opacity: 0.8 }}
        >
          <strong>Image prompt:</strong> {imagePrompt}
        </Typography>
      ) : null}

      {/* dynamic component  */}
      {source && (
        <Box sx={{ mt: 2 }}>
          <DynamicComponent source={source} isAdmin={isAdmin} />
        </Box>
      )}

      {!text && !imageUrl && !imagePrompt && !source && (
        <Box sx={{ textAlign: "center", py: 4, opacity: 0.6 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
}
